import React, { useEffect, useState, useRef } from 'react';
import { USCurrentStyle } from '../../../utils';
import { getMinLoanRequired } from '../../../utils/lenderUtils.ts';

const USNumberFormat = (value) => {
  const numericValue = parseFloat(value.replace(/,/g, '')) || 0;
  return numericValue !== 0 ? USCurrentStyle(numericValue) : '';
};

const ApprovedAmountInput = ({ option, active, setOptions }) => {
  const inputRef = useRef(null);
  const { optionKey } = active;
  const { approvedAmount } = option.splitLoan.primaryLender;
  const optionFinancePlan = option.financePlan; // Do NOT use active.financePlan
  const minLoanRequired = getMinLoanRequired(active.lender, optionFinancePlan);
  const { totalFinanced, finalTotalCost, displayedFinanced } = option;
  const [value, setValue] = useState(
    approvedAmount > 0 ? USCurrentStyle(approvedAmount) : ''
  );
  const [error, setError] = useState(null);
  const [revalidate, setRevalidate] = useState(false);

  // Check if the lender requires a down payment
  const isDownPmntRequired = active.lender.downpayment > 0;
  const timeoutRef = useRef(null);

  const handleInputChange = (e) => {
    setError(null);
    if (error !== null) setError(null);
    let amount = e.target.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters

    if (amount < minLoanRequired) {
      setError(
        `Amount must be at least ${USCurrentStyle(
          minLoanRequired
        )} for the ${optionFinancePlan} month plan`
      );
    }

    const handleExceedsLimit = (limit, limitName) => {
      setError(`Amount cannot exceed ${USCurrentStyle(limit)}`);
      setTimeout(() => {
        setRevalidate(!revalidate); // Just in case the previously entered amount is still less than the min requirement
      }, 3000);
      return;
    };

    // Lenders that require a down payment
    if (isDownPmntRequired) {
      // When Down Payment Input is enabled and updated manually by user
      // displayedFinanced is > 0 is true and visible only when the down payment is updated
      // DO NOT use totalFinanced, finalTotalCost here
      if (displayedFinanced > 0 && amount > displayedFinanced) {
        return handleExceedsLimit(displayedFinanced);
      }

      // This condition won't be met when down payment input is enabled and updated manually by user
      // DO NOT use totalFinanced here, use finalTotalCost instead
      if (amount > finalTotalCost) {
        return handleExceedsLimit(finalTotalCost);
      }
    } else {
      // Lenders that do NOT require a down payment
      if (amount > totalFinanced) {
        return handleExceedsLimit(totalFinanced);
      }
    }

    // Format the value as currency
    const formattedValue = USNumberFormat(amount);

    setValue(formattedValue);

    const updateApprovedAmount = (approvedAmount) => {
      setOptions((prev) => ({
        ...prev,
        [optionKey]: {
          ...option,
          splitLoan: {
            ...option.splitLoan,
            primaryLender: {
              ...option.splitLoan.primaryLender,
              approvedAmount,
            },
          },
        },
      }));
    };

    if (amount >= minLoanRequired) {
      clearTimeout(timeoutRef.current);
      updateApprovedAmount(+amount);
    } else {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        updateApprovedAmount(0);
      }, 5000);
    }
  };

  // When deleting an area, the approved amount can be bigger than the total cost,
  // Therefore, the approvedAmount defaults to 0 in the updateOptionProps.js in that case
  useEffect(() => {
    setError(null);
    if (approvedAmount === 0) setValue('');
    if (
      minLoanRequired &&
      approvedAmount !== 0 &&
      approvedAmount < minLoanRequired
    ) {
      setError(
        `Amount must be at least ${USCurrentStyle(
          minLoanRequired
        )} for the ${optionFinancePlan} month plan`
      );
    }
    // Lenders that require a down payment
    else if (isDownPmntRequired) {
      if (displayedFinanced > 0 && approvedAmount > displayedFinanced) {
        setError(`Amount cannot exceed ${USCurrentStyle(displayedFinanced)}`);
      } else if (approvedAmount > finalTotalCost) {
        setError(`Amount cannot exceed ${USCurrentStyle(finalTotalCost)}`);
      }
    }
    // Lenders that do NOT require a down payment
    else if (!isDownPmntRequired && approvedAmount > totalFinanced) {
      setError(`Amount cannot exceed ${USCurrentStyle(totalFinanced)}`);
    } else {
      setError(null);
    }
  }, [
    approvedAmount,
    minLoanRequired,
    optionFinancePlan,
    totalFinanced,
    revalidate,
  ]);

  useEffect(() => {
    // Focus the input element when the component mounts
    inputRef.current.focus();
  }, []);

  return (
    <div className='row gy-2 pb-3 align-items-center print-hide'>
      <div className='col-xxl-5'>
        <p className='mb-0 lh-1'>
          <small>{active.lender.name}</small>
        </p>
      </div>
      <div className='col-xxl-7'>
        <div>
          <input
            ref={inputRef}
            type='text'
            value={value}
            onChange={handleInputChange}
            className={`form-control currency-input text-center mx-auto ${
              error ? 'border-danger milan-text-red' : ''
            }`}
            placeholder='$0,000'
          />
        </div>
      </div>
      {error && (
        <p className='milan-text-red lh-1 mt-1 mb-0'>
          <small>
            <small>{error}</small>{' '}
          </small>
        </p>
      )}
    </div>
  );
};

export default ApprovedAmountInput;
